/* eslint-disable @typescript-eslint/no-non-null-assertion, deprecation/deprecation */
import {
  AlertProps,
  capitalize,
  iconButtonClasses,
  snackbarClasses,
  styled,
  svgIconClasses,
  useThemeProps,
} from '@mui/material';
import clsx from 'clsx';
import * as React from 'react';
import {
  Alert,
  type AlertProps as UnifyAlertProps,
} from 'src/components/Alert/Alert';
import { getAlertBaseUtilityClass } from 'src/components/AlertBase/alertBaseClasses';
import { getAlertBaseStyledOverridesResolver } from 'src/components/AlertBase/styledAlertBaseOverridesResolver';
import UnifyIcons from 'src/components/UnifyIcons';
import SassColor from 'src/utils/sass_color_utils';
import buttonClasses from 'src/wsd/components/Button/buttonClasses';
import alertClasses from 'src/wsd/components/WsdAlert/alertClasses';
import {
  wsdAlertPalettes,
  wsdAlertToastBgAlpha,
} from 'src/wsd/components/WsdAlert/style/palette';
import {
  alertCloseButtonHeight,
  alertCloseButtonVerticalPadding as alertCloseButtonVerticalPaddingFn,
  alertMessageVerticalPadding as alertMessageVerticalPaddingFn,
  alertMinHeights,
  alertVerticalPadding as alertVerticalPaddingFn,
} from 'src/wsd/components/WsdAlert/style/size';
import useLegacyWsdStyles from 'src/wsd/styles/context/useLegacyWsdStyles';
import wkTheme from 'src/wsd/styles/theme/wkTheme';

const defaultIconMapping = {
  success: (
    <UnifyIcons.CheckCircle
      fontSize="inherit"
      data-testid="CheckCircleOutlineIcon"
    />
  ),
  warning: <UnifyIcons.Warning fontSize="inherit" />,
  error: (
    <UnifyIcons.DoNotDisturbOn
      fontSize="inherit"
      data-test-id="unify.icons.error"
    />
  ),
  info: <UnifyIcons.Info fontSize="inherit" />,
};

type WsdAlertOwnProps = {
  /**
   * The size variation that changes the font size and
   * amount of white space within the `Alert`.
   *
   * @default 'medium'
   */
  // FIXME: remove this prop when we un-WSD-ify this component. UI team says there won't be sizes.
  size?: 'medium' | 'small' | 'xsmall';
};

type WsdAlertOwnerState = WsdAlertOwnProps & {
  isWsdAlert: boolean;
};

const WsdAlertRoot = styled(Alert, {
  name: 'WsdAlert',
  slot: 'Root',
  overridesResolver: getAlertBaseStyledOverridesResolver('root'),
})<
  Omit<UnifyAlertProps, 'severity'> &
    Required<Pick<UnifyAlertProps, 'severity'>> & {
      ownerState: WsdAlertOwnerState;
    } & Pick<AlertProps, 'iconMapping'>
>(function WsdAlertRoot(stylePropsIn) {
  const { severity, variant, ownerState } = stylePropsIn;
  const { isWsdAlert, size } = ownerState;
  // The styles in here are only for legacy WSD Alert.
  // Unify Alert styles can be found in src/components/Alert/Alert.
  if (!isWsdAlert) return {};
  // Use wkTheme directly to get old theme values that are no longer
  // available from themeContext since ThemeProvider will soon stop
  // rendering a MUI ThemeProvider under the hood. See: FED-3568
  // eslint-disable-next-line deprecation/deprecation
  const theme = wkTheme;

  const { lineHeight, fontSize } = theme.typography;
  const alertVerticalPadding = alertVerticalPaddingFn(theme);
  const alertMessageVerticalPadding = alertMessageVerticalPaddingFn(theme);
  const alertCloseButtonVerticalPadding =
    alertCloseButtonVerticalPaddingFn(theme);

  const alertSizeVariationStyles = {
    ...(size === 'small' && {
      minHeight: alertMinHeights.small,
      padding: `${alertVerticalPadding.small}px 9px`,
      paddingLeft: 12,
    }),

    ...(size === 'xsmall' && {
      minHeight: alertMinHeights.xsmall,
      padding: `${alertVerticalPadding.xsmall}px 9px`,
      paddingLeft: 12,
      lineHeight: 1.2,
      fontSize: 12,
    }),

    [`& .${alertClasses.icon}`]: {
      ...(size === 'small' && {
        padding: `${alertMessageVerticalPadding.small}px 0`,
        fontSize: 20,

        [`& .${svgIconClasses.root}`]: {
          fontSize: 'inherit',
        },
      }),

      ...(size === 'xsmall' && {
        padding: `${alertMessageVerticalPadding.xsmall * 2}px 0`,
        fontSize: 20,

        [`& .${svgIconClasses.root}`]: {
          fontSize: 'inherit',
        },
      }),
    },

    [`& .${alertClasses.message}`]: {
      textAlign: 'left',
      alignSelf: 'center',
      ...(isWsdAlert && {
        padding: `${alertMessageVerticalPadding.medium}px 0`,
      }),

      ...(size === 'small' && {
        padding: `${alertMessageVerticalPadding.small}px 0`,
      }),

      ...(size === 'xsmall' && {
        padding: `${alertMessageVerticalPadding.xsmall}px 0`,
      }),
    },

    [`& .${alertClasses.action}`]: {
      ...(size === 'small' && {
        paddingTop: Math.ceil(alertMessageVerticalPadding.small / 2),
        paddingLeft: 12,
        marginRight: 0,
      }),

      ...(size === 'xsmall' && {
        paddingTop: Math.ceil(alertMessageVerticalPadding.xsmall / 2),
        paddingLeft: 12,
        marginRight: 0,
      }),

      [`> .${iconButtonClasses.root}`]: {
        ...(size === 'small' && {
          fontSize: 16,
        }),

        ...(size === 'xsmall' && {
          fontSize: 16,
        }),

        [`& .${svgIconClasses.root}`]: {
          fontSize: 'inherit',
        },
      },
    },
  };

  const { bgStandard, bgFilled, bdLeft } = wsdAlertPalettes[severity](theme);
  const { grey, getContrastText } = theme.palette;
  // Widen the type of `variant` to avoid warnings when type checking wsd options that don't
  // technically exist in the union, but could be passed through the Dart wrapper.
  // We can remove support for this once we stop supporting these options in Dart.
  const wsdVariant: unknown = variant;

  return {
    border: 'none',
    borderRadius: 0,
    color: theme.palette[severity].dark,
    minHeight: alertMinHeights.medium,
    marginBottom: theme.spacing(1.25),
    padding: `${alertVerticalPadding.medium}px 17px`,
    paddingLeft: 20,
    fontSize: fontSize,
    letterSpacing: 'normal',
    lineHeight: lineHeight,

    ['p, ul, ol']: {
      margin: 0,
    },

    ...alertSizeVariationStyles,

    // Make a Mui IconButton look like a WSD "Close Button"
    [`& .${alertClasses.action}`]: {
      paddingTop: Math.ceil(alertMessageVerticalPadding.medium / 2),
      paddingLeft: 20,
      marginRight: 0,

      ...(size === 'small' && {
        paddingTop: Math.ceil(alertMessageVerticalPadding.small / 2),
        paddingLeft: 12,
      }),

      ...(size === 'xsmall' && {
        paddingTop: Math.ceil(alertMessageVerticalPadding.xsmall / 2),
        paddingLeft: 12,
      }),

      [`> .${iconButtonClasses.root}`]: {
        fontSize: alertCloseButtonHeight,
        opacity: 0.3,
        padding: `${alertCloseButtonVerticalPadding.medium}px 0`,

        ...(size === 'small' && {
          padding: `${alertCloseButtonVerticalPadding.small}px 0`,
        }),

        ...(size === 'xsmall' && {
          padding: `${alertCloseButtonVerticalPadding.xsmall}px 0`,
        }),

        [`&:hover, &.${buttonClasses.focusVisible}`]: {
          backgroundColor: 'transparent',
          opacity: 0.6,
        },

        '&:active': {
          backgroundColor: 'transparent',
          opacity: 0.8,
        },

        [`& .${svgIconClasses.root}`]: {
          fontSize: 'inherit',
        },
      },
    },

    //
    // "Standard" Variant
    //
    ...(variant === 'standard' && {
      [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
        backgroundColor: bgStandard.default,
        border: 'none',
        borderLeft: `3px solid ${bdLeft.default}`,
        color: getContrastText(bgStandard.default),
      },
    }),
    ...(wsdVariant === 'wsdGray' && {
      [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
        backgroundColor: grey[300],
        border: 'none',
        borderLeft: `3px solid ${grey[700]}`,
        color: getContrastText(grey[300]),
      },
    }),
    //
    // "Filled" Variant ("Toast Alerts")
    //
    ...((wsdVariant === 'filled-wsdGray' || variant === 'filled') && {
      position: 'fixed',
      left: 0,
      right: 0,
      top: 0,
      bottom: 'auto',
      width: '100%',
      paddingLeft: 17,

      [`.${snackbarClasses.root} &`]: {
        position: 'initial',
      },

      [`.${alertClasses.message}`]: {
        flex: 1,
        textAlign: 'center',

        '.alert-link': {
          color: 'inherit',
          textDecoration: 'underline',
        },
      },

      ...(size === 'small' && {
        paddingLeft: 9,
      }),

      ...(size === 'xsmall' && {
        paddingLeft: 9,
      }),
    }),

    ...(variant === 'filled' && {
      [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
        border: 'none',
        backgroundColor: bgFilled.default,
        color: theme.palette.getContrastText(bgFilled.default),
      },
    }),
    ...(wsdVariant === 'filled-wsdGray' && {
      [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
        border: 'none',
        backgroundColor: SassColor.rgba(
          theme.palette.grey[800],
          wsdAlertToastBgAlpha
        ),
        color: theme.palette.getContrastText(theme.palette.grey[800]),
      },
    }),
  };
});

/**
 * @deprecated Use {@link AlertProps} instead.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export type WsdAlertProps = Omit<UnifyAlertProps, 'elevation'> &
  WsdAlertOwnProps & {
    /**
     * Unify system elevation level that determines shadow depth when `variant` is not set to `outlined`.
     * Accepts values between 0 and 6 inclusive.
     * @default 0
     */
    elevation?: number;
  } & Pick<AlertProps, 'iconMapping'>;

// The sole purpose of this component is to support the deprecated Dart WsdAlert which has very limited consumer usages.
// Once all consumers are off WsdAlert, we can remove this in the next major.
// DO NOT make changes to this component and instead update the Unify version in `js_package/src/components/Alert/Alert.tsx`.
/**
 * @deprecated Use {@link Alert} instead.
 */
export const WsdAlert = React.forwardRef<HTMLDivElement, WsdAlertProps>(
  function WsdAlert(inProps, forwardedRef) {
    const legacyWsdStyleSupport = useLegacyWsdStyles();
    const props = useThemeProps({ props: inProps, name: 'MuiAlert' });
    const {
      children,
      className,
      color: colorProp,
      severity = legacyWsdStyleSupport ? 'info' : 'success',
      icon = legacyWsdStyleSupport ? false : undefined,
      iconMapping = defaultIconMapping,
      size = 'medium',
      variant = 'standard',
      ...rest
    } = props;

    const classes = clsx(
      {
        [alertClasses.sizeMedium]: size === 'medium',
        [alertClasses.sizeSmall]: size === 'small',
        [alertClasses.sizeXsmall]: size === 'xsmall',
      },
      className
    );

    // Things we need in our styled component,
    // but don't get forwarded to the underlying MUI alert
    const unifyOwnerState = {
      size,
      isWsdAlert: legacyWsdStyleSupport,
    };

    return (
      <WsdAlertRoot
        icon={icon}
        color={colorProp}
        severity={colorProp ?? severity}
        ownerState={unifyOwnerState}
        iconMapping={iconMapping}
        variant={variant}
        className={classes}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </WsdAlertRoot>
    );
  }
);

export default WsdAlert;
